import {
  CombinedLevel,
  CommunityGrowthCommissionItem,
  DailyCommissionDetailItem,
  DailyCommissionItem,
  FlowCommissionItem,
  InfinityCommissionDetailItem,
  InfinityCommissionItem,
  LoyaltyReferralCommissionItem,
  Milestone,
  MilestoneCommissionItem,
  PartnerGrowthIncentiveItem,
  PearlCommissionItem,
  ReferralCommissionItem,
  TreeNode,
  UnityCommissionItem,
} from "../../../Redux/referral/types";

// ---------- Shared formatters ----------

export const formatEarningsCurrency = (value?: number | null) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value ?? 0);

export const formatEarningsNumber = (value?: number | null) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    value ?? 0,
  );

const parseDate = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatEarningsDate = (value?: string | null) => {
  const d = parseDate(value);
  if (!d) return value || "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
    .format(d)
    .replace(/\//g, "-");
};

export const formatEarningsLongDate = (value?: string | null) => {
  const d = parseDate(value);
  if (!d) return value || "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
    .format(d)
    .replace(/ /g, "-")
    .toUpperCase();
};

export const formatEarningsTime = (value?: string | null) => {
  const d = parseDate(value);
  if (!d) return "";
  return (
    new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(d) + " IST"
  );
};

// ---------- Status helpers ----------

export type EarningsStatusSlug =
  | "completed"
  | "success"
  | "available"
  | "pending"
  | "cancelled"
  | "expired"
  | "used"
  | "failed";

export const normalizeEarningsStatus = (
  status?: string | null,
): EarningsStatusSlug => {
  const s = (status || "").trim().toLowerCase();
  if (["completed", "complete", "success", "received", "credited"].includes(s))
    return "completed";
  if (["available", "active"].includes(s)) return "available";
  if (["used"].includes(s)) return "used";
  if (["expired"].includes(s)) return "expired";
  if (["cancelled", "canceled", "rejected", "failed"].includes(s))
    return "cancelled";
  return "pending";
};

export const statusLabel = (status?: string | null) => {
  if (!status) return "Pending";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const earningsStatusClass = (status?: string | null) =>
  `ri-status ri-status-${normalizeEarningsStatus(status)}`;

export const toSlug = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");

export const statusChipClass = (status?: string | null) =>
  `${normalizeEarningsStatus(status)}`;

export const PRODUCT_IMAGE_FALLBACK = "../../assets/img/icons/awards.png";
export const AVATAR_FALLBACK = "../../assets/img/team-2.jpg";

// ---------- Salar Flow (flowCommission) ----------

export const mapFlowItemToRecord = (item: FlowCommissionItem) => ({
  id: item.orderNo || `${item.sNo}`,
  serialNo: item.sNo,
  orderDate: item.orderDate,
  orderDateFormatted: item.orderDateFormatted,
  orderTime: item.orderTime,
  orderNo: item.orderNo,
  productName: item.productDetails?.productName || "",
  orderType: item.productDetails?.orderType || "",
  sfNetworkNo: item.productDetails?.sfNetworkNo || "",
  uplinerId: item.uplinerId || item.productDetails?.uplinerId || "",
  uplinerName: item.uplinerName || item.productDetails?.uplinerName || "",
  contributionRequirement: item.productDetails?.contributionRequirement ?? 0,
  activeContributors: item.productDetails?.activeContributors ?? 0,
  flowStatus: item.referralNetworkStatus?.status || "Pending",
  referralIncentives: item.referralIncentives ?? 0,
  novaContributionRequirement:
    item.novaReferralNetwork?.contributionRequirement ?? 0,
  novaActiveContributors: item.novaReferralNetwork?.activeContributors ?? 0,
  novaEarnedIncentives: item.novaReferralNetwork?.earnedIncentives ?? 0,
  achieved: item.achieved,
  achievedDate: item.achievedDate,
  level: item.level,
  depth: item.depth,
  width: item.width,
  salarCoins: item.salarCoins ?? 0,
  shoppingAmount: item.shoppingAmount ?? 0,
  sponsorCommission: item.sponsorCommission ?? 0,
  aurCommission: item.aurCommission ?? 0,
  totalIncentive: item.totalIncentive ?? 0,
  grossAmount: item.grossAmount ?? item.totalIncentive ?? 0,
  tdsPercentage: item.tdsPercentage,
  tdsAmount: item.tdsAmount,
  netAmount: item.netAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  referralIncentivesTds: item.referralIncentivesTds,
  referralIncentivesNet: item.referralIncentivesNet,
  sponsorCommissionTds: item.sponsorCommissionTds,
  sponsorCommissionNet: item.sponsorCommissionNet,
  aurCommissionTds: item.aurCommissionTds,
  aurCommissionNet: item.aurCommissionNet,
  status: item.status,
  canWithdraw: item.canWithdraw,
  canTransfer: item.canTransfer,
});

export type FlowRecord = ReturnType<typeof mapFlowItemToRecord>;

// ---------- Referral network milestones / members ----------

export const mapMilestoneToRow = (milestone: Milestone, index: number) => ({
  id: `${milestone.milestoneNo || index}`,
  milestoneNo: milestone.milestoneNo || index + 1,
  milestoneName:
    milestone.milestoneName ||
    `Level ${milestone.milestoneNo || index + 1} Milestone`,
  productName: milestone.productName || "",
  contributionRequirement: milestone.contributionRequirement ?? 0,
  activeContributors: milestone.activeContributors ?? 0,
  status: milestone.status,
  members: milestone.members || [],
});

export type MilestoneRow = ReturnType<typeof mapMilestoneToRow>;

export const mapCombinedLevelToRow = (level: CombinedLevel) => ({
  level: level.level,
  productName: level.productName,
  requiredNo: level.requiredNo ?? 0,
  joinedMembers: level.joinedMembers ?? 0,
  commissions: level.commissions ?? 0,
  isCompleted: level.isCompleted,
});

export type CombinedLevelRow = ReturnType<typeof mapCombinedLevelToRow>;

// ---------- Loyalty Referral Incentives ----------

export const mapLoyaltyItemToRecord = (item: LoyaltyReferralCommissionItem) => ({
  id: `${item.sNo}-${item.customerId}`,
  serialNo: item.sNo,
  date: item.date,
  incentiveType: item.incentiveType || "Loyalty Referral",
  partnerNetworkId: item.customerId || "—",
  partnerNetworkName: item.customerName || "—",
  partnerLocation: item.customerLocation || "",
  orderNo: item.orderDetails?.orderNo || "",
  productName: item.productDetails?.productName || "",
  productImage: item.productDetails?.productImage || "",
  receivedIncentive:
    item.receivedLoyaltyReferralCommission ??
    item.receivedReferralCommission ??
    item.aurCommission ??
    0,
  grossAmount:
    item.grossAmount ??
    item.receivedLoyaltyReferralCommission ??
    item.receivedReferralCommission ??
    item.aurCommission ??
    0,
  tdsPercentage: item.tdsPercentage,
  tdsAmount: item.tdsAmount,
  netAmount: item.netAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  status:
    item.loyaltyReferralCommissionStatus ||
    item.referralCommissionStatus ||
    "Pending",
});

export type LoyaltyRecord = ReturnType<typeof mapLoyaltyItemToRecord>;

// ---------- Referral Incentives ----------

export const mapReferralIncentiveItem = (item: ReferralCommissionItem) => ({
  id: `${item.sNo}-${item.customerId}`,
  serialNo: item.sNo,
  date: item.date,
  incentiveType: item.incentiveType || "Referral",
  partnerNetworkId: item.customerId || "—",
  partnerNetworkName: item.customerName || "—",
  partnerLocation: item.customerLocation || "",
  orderNo: item.orderDetails?.orderNo || "",
  productName: item.productDetails?.productName || "",
  productImage: item.productDetails?.productImage || "",
  level: item.level,
  sponsorCommission: item.sponsorCommission ?? 0,
  aurCommission: item.aurCommission ?? 0,
  receivedIncentive: item.receivedReferralCommission ?? 0,
  grossAmount: item.grossAmount ?? item.receivedReferralCommission ?? 0,
  tdsPercentage: item.tdsPercentage,
  tdsAmount: item.tdsAmount,
  netAmount: item.netAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  status: item.referralCommissionStatus || "Pending",
});

export type ReferralIncentiveRecord = ReturnType<
  typeof mapReferralIncentiveItem
>;

// ---------- Milestone Incentives (shopping amount) ----------

export const mapMilestoneCommissionItem = (item: MilestoneCommissionItem) => ({
  id: `${item.sNo}-${item.orderDetails?.orderNo}`,
  recordId: item.recordId || "",
  serialNo: item.sNo,
  date: item.date,
  orderId: item.orderDetails?.orderNo || "",
  orderDate: item.orderDetails?.orderDate || "",
  orderType: item.orderDetails?.orderTypeLabel || item.orderDetails?.orderType || "",
  referralPlan: item.orderDetails?.referralPlan || "",
  achievedDate: item.creditedAt || item.date,
  status: item.receivedMilestoneCommissionStatus || "Pending",
  productName: item.productDetails?.productName || "",
  productImage: item.productDetails?.productImage || "",
  quantity: item.productDetails?.qty ?? 1,
  grandTotal: item.productDetails?.productPrice ?? 0,
  voucherAmount: item.productDetails?.voucherAmount ?? 0,
  customerName: item.customerName || "",
  customerLocation: item.customerLocation || "",
  milestoneName: item.milestone || `Milestone - ${item.milestoneNumber}`,
  milestoneLevel: item.milestoneLevel ?? 0,
  commissionType: item.commissionTypeLabel || item.commissionType || "",
  levelNo: item.receivedLevelDetails?.levelNo ?? item.milestoneLevel ?? 0,
  levelCommission: item.receivedLevelDetails?.receivedMilestoneCommission ?? 0,
  grossAmount: item.grossAmount ?? 0,
  tdsAmount: item.tdsAmount ?? 0,
  tdsPercentage: item.tdsPercentage,
  adminFeeAmount: item.adminFeeAmount ?? 0,
  netAmount: item.netAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  receivedMilestoneCommission: item.receivedMilestoneCommission ?? 0,
  bonusAmount: item.bonusDetails?.bonusAmount ?? 0,
  bonusNetAmount: item.bonusDetails?.bonusNetAmount ?? 0,
  targetDays: item.bonusDetails?.targetDays ?? 0,
  actualDays: item.bonusDetails?.actualDays ?? 0,
  completedWithinTarget: item.bonusDetails?.completedWithinTarget ?? false,
  canWithdraw: item.canWithdraw,
  canTransfer: item.canTransfer,
});

export type MilestoneIncentiveRecord = ReturnType<
  typeof mapMilestoneCommissionItem
>;

// ---------- Samriddhi / Infinity commissions ----------

export const mapInfinityItemToSummary = (item: InfinityCommissionItem) => ({
  id: item._id,
  monthYear: item.monthYear,
  monthYearLabel: item.monthYearLabel,
  month: item.month,
  year: item.year,
  status: item.status,
  totalCommissionEarned: item.totalCommissionEarned ?? 0,
  totalPendingCommission: item.totalPendingCommission ?? 0,
  totalCancelledCommission: item.totalCancelledCommission ?? 0,
  totalPurchases: item.totalPurchases ?? 0,
  grossAmount: item.grossAmount,
  tdsAmount: item.tdsAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  createdAt: item.createdAt,
});

export type SamriddhiSummary = ReturnType<typeof mapInfinityItemToSummary>;

export const mapInfinityDetailToContributor = (
  item: InfinityCommissionDetailItem,
  index: number,
) => ({
  id: item._id || `${index}`,
  serialNo: index + 1,
  date: item.creditedAt || item.createdAt,
  contributorName:
    item.purchaserName || item.purchaserDetails?.name || item.name || "—",
  contributorId:
    item.purchaserRegisterId ||
    item.purchaserDetails?.registerId ||
    item.purchaserId ||
    "—",
  orderNo: item.orderNo,
  productName: item.productName || item.productDetails?.productName || "",
  productPrice: item.productPrice ?? 0,
  levelDistance: item.levelDistance ?? 0,
  grossAmount: item.commissionAmount ?? 0,
  tdsAmount: item.tdsAmount ?? 0,
  tdsPercentage: item.tdsPercentage,
  adminFeeAmount: item.adminFeeAmount ?? 0,
  netAmount: item.netAmount ?? 0,
  tdsIsEstimated: item.tdsIsEstimated,
  status: item.status,
});

export type SamriddhiContributor = ReturnType<
  typeof mapInfinityDetailToContributor
>;

// ---------- Community Growth ----------

export const mapCommunityItemToRecord = (
  item: CommunityGrowthCommissionItem,
) => ({
  id: `${item.sNo}-${item.orderNo || ""}`,
  serialNo: item.sNo,
  date: item.date,
  achievedDate: item.achievedDate || item.date,
  partnerNetworkId: item.receivedFrom || item.customerId || "—",
  orderId: item.orderNo || "—",
  milestone: item.referralMilestone || "—",
  status: item.receivedStatus || "Pending",
  productName: item.productDetails?.productName || "",
  productImage: item.productDetails?.image || "",
  voucherAmount: item.productDetails?.price ?? 0,
  salarCode: item.productDetails?.salarCode || "",
  receivedSCGRIncentive: item.receivedSCGRCommission ?? 0,
  grossAmount: item.grossAmount,
  tdsAmount: item.tdsAmount,
  tdsPercentage: item.tdsPercentage,
  adminFeeAmount: item.adminFeeAmount,
  netAmount: item.netAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  bonusLevelNo: item.bonusDetails?.levelNo ?? 0,
  receivedBonusSCGRIncentive:
    item.bonusDetails?.bonusReceivedCommunityGrowthCommission ?? 0,
});

export type CommunityGrowthRecord = ReturnType<typeof mapCommunityItemToRecord>;

// ---------- Daily Incentives ----------

export const mapDailyItemToPeriod = (item: DailyCommissionItem) => ({
  id: `${item.monthYearCode}`,
  serialNo: item.sNo,
  monthYear: item.monthYear,
  monthYearCode: item.monthYearCode,
  year: item.year,
  month: item.month,
  contributors: item.contributors ?? 0,
  grossAmount: item.grossAmount,
  tdsPercentage: item.tdsPercentage,
  tdsAmount: item.tdsAmount,
  netAmount: item.netAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  earnedIncentives: item.receivedIncentives ?? 0,
  pendingIncentives: item.pendingIncentives ?? 0,
  cancelledIncentives: item.cancelledIncentives ?? 0,
});

export type DailyPeriod = ReturnType<typeof mapDailyItemToPeriod>;

export const mapDailyDetailToEntry = (
  item: DailyCommissionDetailItem,
  index: number,
) => ({
  id: `${item.sNo}-${item.dateRaw || item.date}`,
  serialNo: item.sNo || index + 1,
  date: item.date,
  dateRaw: item.dateRaw,
  contributors: item.contributors ?? 0,
  grossAmount: item.grossAmount,
  tdsPercentage: item.tdsPercentage,
  tdsAmount: item.tdsAmount,
  netAmount: item.netAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  earnedIncentives: item.receivedIncentives ?? 0,
  partnerGrowthIncentive: item.partnerGrowthReferralIncentive ?? 0,
});

export type DailyEntry = ReturnType<typeof mapDailyDetailToEntry>;

// ---------- Salar Pearls ----------

export const mapPearlItemToRecord = (item: PearlCommissionItem) => ({
  id: `${item.sNo}-${item.orderId || ""}`,
  serialNo: item.sNo,
  date: item.dateRaw || item.date,
  dateFormatted: item.date,
  orderId: item.orderId || "—",
  achievedDate: item.achievedDateRaw || item.achievedDate,
  achievedDateFormatted: item.achievedDate,
  status: item.salarPearlStatus || item.status || "Available",
  purchasedOn: item.purchasedOn,
  productName: item.productName || item.purchaseTitle || "",
  productImage: item.productImage || "",
  voucherAmount: item.salarShoppingVoucher ?? 0,
  milestoneName: item.milestoneName || "",
  salarPearls: item.salarPearls ?? 0,
  level: item.level ?? 0,
  customerId: item.customerId || "",
  customerName: item.customerName || "",
  validityDays: item.validityDays ?? 0,
  displayDays: item.displayDays ?? 0,
});

export type SalarPearlRecord = ReturnType<typeof mapPearlItemToRecord>;

// ---------- Salar Unity ----------

export const mapUnityItemToRecord = (item: UnityCommissionItem) => ({
  id: `${item.sNo}-${item.partnerNetworkId || ""}`,
  serialNo: item.sNo,
  achievedDate: item.achievedDate || item.date,
  partnerNetworkId: item.partnerNetworkId || "—",
  milestone: item.achievementDetails?.milestone || "—",
  milestoneLevel: item.achievementDetails?.level ?? item.level ?? 0,
  status: item.receivedCommissionStatus || item.status || "Pending",
  productName: item.productName || "",
  productImage: item.productImage || "",
  voucherAmount: item.productPrice ?? 0,
  salarCode: item.salarCode || "",
  purchaserId: item.purchaserId || "",
  purchaserName: item.purchaserName || "",
  grossAmount: item.grossAmount ?? 0,
  tdsAmount: item.tdsAmount ?? 0,
  tdsPercentage: item.tdsPercentage,
  adminFeeAmount: item.adminFeeAmount ?? 0,
  netAmount: item.netAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  receivedUnityCommission: item.receivedUnityCommission ?? 0,
  receivedSURIncentive: item.receivedIncentives?.receivedSURIncentive ?? 0,
  receivedIncentiveMilestone: item.receivedIncentives?.milestone || "",
  bonusTargetDays: item.bonusSURIncentive?.targetDays ?? 0,
  bonusTargetAchievedDays: item.bonusSURIncentive?.targetAchievedDays ?? 0,
  receivedBonusSURIncentive:
    item.bonusSURIncentive?.receivedBonusSURIncentive ?? 0,
  bonusMilestone: item.bonusSURIncentive?.milestone || "",
});

export type SalarUnityRecord = ReturnType<typeof mapUnityItemToRecord>;

// ---------- Partner Growth Incentives ----------

export const mapPartnerGrowthIncentive = (
  item: PartnerGrowthIncentiveItem,
) => ({
  id: `${item.sNo}-${item.myPartnerRegisterId || ""}`,
  serialNo: item.sNo,
  date: item.creditedAt || item.date,
  partnerName: item.myPartnerName || "—",
  partnerId: item.myPartnerRegisterId || "",
  incentive: item.myPartnerGrowthReferralIncentives ?? 0,
  grossAmount: item.grossAmount ?? 0,
  tdsAmount: item.tdsAmount,
  tdsPercentage: item.tdsPercentage,
  adminFeeAmount: item.adminFeeAmount,
  netAmount: item.netAmount,
  tdsIsEstimated: item.tdsIsEstimated,
  status: item.status || "Pending",
  creditedAt: item.creditedAt,
});

export type PartnerGrowthIncentiveRecord = ReturnType<
  typeof mapPartnerGrowthIncentive
>;

// ---------- Referral Tree ----------

const isObjectIdLike = (value?: string | null): boolean =>
  /^[a-fA-F0-9]{24}$/.test((value || "").trim());

const displayText = (value?: string | null): string =>
  value && !isObjectIdLike(value) ? value : "";

export const mapTreeNodeToDisplay = (node: TreeNode): ReferralTreeNode => ({
  id:
    node.memberId ||
    node.networkId ||
    node.salarCode ||
    node.memberName,
  name:
    node.memberDetails?.fullName || displayText(node.memberName) || "Member",
  userId:
    node.memberDetails?.registerId ||
    displayText(node.salarCode) ||
    displayText(node.networkId),
  memberId: node.memberId,
  memberDetails: node.memberDetails,
  photo: node.memberDetails?.profilePicture || node.memberPhoto || "",
  status: node.status,
  isCompleted: node.isCompleted,
  commissionAmount: node.commissionAmount,
  salarCoins: node.salarCoins,
  children: (node.children || []).map(mapTreeNodeToDisplay),
});

export interface ReferralTreeNode {
  id: string;
  name: string;
  userId: string;
  memberId?: string;
  memberDetails?: TreeNode["memberDetails"];
  photo?: string;
  status?: string;
  isCompleted?: boolean;
  commissionAmount?: number;
  salarCoins?: number;
  children: ReferralTreeNode[];
}
